import * as React from 'react';
import { createContext, useContext, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
    OutputMapperConfig,
    FieldMapping,
    Action,
    ActionType,
    TypeDefinition,
    FieldDefinition,
    AliasContextEntry,
    ActionTypeInfo,
    TransformFunctionInfo,
} from './types.ts';
import { FIELD_TYPES, CONDITION_OPERATORS, TRANSFORM_FUNCTIONS } from './types.ts';
import { createDefaultAction } from './utils/actionDefaults.ts';
import { updateFieldsAtPath } from './utils/fieldTreeUtils.ts';
import { toHumanReadable } from '../../../core/utils/string.ts';

const ACTION_TYPES_FALLBACK: ActionTypeInfo[] = [
    { value: 'extract_source', label: 'Extract Source', description: 'Extract value from input field' },
    { value: 'assign_literal', label: 'Assign Literal', description: 'Set a hardcoded value' },
    { value: 'transform', label: 'Transform', description: 'Apply transformation function' },
    { value: 'function_call', label: 'Function Call', description: 'Call custom function' },
    { value: 'conditional', label: 'Conditional', description: 'Branch based on condition' },
    { value: 'map_array', label: 'Map Array', description: 'Map over array items' },
];

export interface InputFieldOption {
    path: string;
    description?: string;
}

interface OutputMapperEditorContextValue {
    config: OutputMapperConfig;
    setConfig: React.Dispatch<React.SetStateAction<OutputMapperConfig>>;
    isDirty: boolean;
    selectedMappingIndex: number | null;
    setSelectedMappingIndex: (index: number | null) => void;
    selectedMapping: FieldMapping | null;
    addMapping: (target: string) => void;
    updateMapping: (index: number, mapping: Partial<FieldMapping>) => void;
    deleteMapping: (index: number) => void;
    moveMapping: (fromIndex: number, toIndex: number) => void;
    addAction: (mappingIndex: number, actionType: ActionType, insertIndex?: number) => void;
    updateAction: (mappingIndex: number, actionIndex: number, action: Partial<Action>) => void;
    deleteAction: (mappingIndex: number, actionIndex: number) => void;
    moveAction: (mappingIndex: number, fromIndex: number, toIndex: number) => void;
    addCustomField: (typeName: string, fieldPath: string[], field: FieldDefinition) => void;
    removeCustomField: (typeName: string, fieldPath: string[]) => void;
    getInputFieldPaths: (aliasContext?: AliasContextEntry[]) => string[];
    getInputFields: (aliasContext?: AliasContextEntry[]) => InputFieldOption[];
    flattenFields: (fields: FieldDefinition[], prefix?: string) => string[];
    availableActionTypes: ActionTypeInfo[];
    fieldTypes: { value: string; label: string }[];
    conditionOperators: { value: string; label: string }[];
    transformFunctions: TransformFunctionInfo[];
}

const OutputMapperEditorContext = createContext<OutputMapperEditorContextValue | null>(null);

export const useOutputMapperEditor = () => {
    const context = useContext(OutputMapperEditorContext);
    if (!context) {
        throw new Error('useOutputMapperEditor must be used within OutputMapperEditorProvider');
    }
    return context;
};

interface OutputMapperEditorProviderProps {
    initialConfig: OutputMapperConfig;
    /** Target to select first (e.g. coming from the template editor); a nested `parent[].child` selects `parent`. */
    initialTarget?: string | null;
    children: React.ReactNode;
}

const initialMappingIndex = (config: OutputMapperConfig, target?: string | null): number | null => {
    if (config.output_mapping.length === 0) return null;
    const topLevelTarget = target?.split('[]')[0];
    const index = topLevelTarget ? config.output_mapping.findIndex((m) => m.target === topLevelTarget) : -1;
    return index >= 0 ? index : 0;
};

export const OutputMapperEditorProvider = ({
    initialConfig,
    initialTarget,
    children,
}: OutputMapperEditorProviderProps) => {
    const { t } = useTranslation('integrations');
    const [config, setConfig] = useState<OutputMapperConfig>(initialConfig);
    const [selectedMappingIndex, setSelectedMappingIndex] = useState<number | null>(() =>
        initialMappingIndex(initialConfig, initialTarget)
    );
    const [isDirty, setIsDirty] = useState(false);

    const selectedMapping = useMemo(() => {
        if (selectedMappingIndex === null) return null;
        return config.output_mapping[selectedMappingIndex] || null;
    }, [config.output_mapping, selectedMappingIndex]);

    const updateConfig = useCallback((updater: (prev: OutputMapperConfig) => OutputMapperConfig) => {
        setConfig((prev) => {
            const next = updater(prev);
            setIsDirty(true);
            return next;
        });
    }, []);

    const addMapping = useCallback(
        (target: string) => {
            updateConfig((prev) => ({
                ...prev,
                output_mapping: [...prev.output_mapping, { target, actions: [], is_required: true }],
            }));
            setSelectedMappingIndex(config.output_mapping.length);
        },
        [config.output_mapping.length, updateConfig]
    );

    const updateMapping = useCallback(
        (index: number, updates: Partial<FieldMapping>) => {
            updateConfig((prev) => ({
                ...prev,
                output_mapping: prev.output_mapping.map((m, i) => (i === index ? { ...m, ...updates } : m)),
            }));
        },
        [updateConfig]
    );

    const deleteMapping = useCallback(
        (index: number) => {
            updateConfig((prev) => ({
                ...prev,
                output_mapping: prev.output_mapping.filter((_, i) => i !== index),
            }));
            if (selectedMappingIndex === index) {
                setSelectedMappingIndex(index > 0 ? index - 1 : config.output_mapping.length > 1 ? 0 : null);
            } else if (selectedMappingIndex !== null && selectedMappingIndex > index) {
                setSelectedMappingIndex(selectedMappingIndex - 1);
            }
        },
        [config.output_mapping.length, selectedMappingIndex, updateConfig]
    );

    const moveMapping = useCallback(
        (fromIndex: number, toIndex: number) => {
            if (fromIndex === toIndex) return;
            updateConfig((prev) => {
                const copy = [...prev.output_mapping];
                const [removed] = copy.splice(fromIndex, 1);
                copy.splice(toIndex, 0, removed);
                return { ...prev, output_mapping: copy };
            });
            // Keep the selection pointing at the same mapping after the reorder.
            setSelectedMappingIndex((prev) => {
                if (prev === null) return prev;
                if (prev === fromIndex) return toIndex;
                if (fromIndex < prev && toIndex >= prev) return prev - 1;
                if (fromIndex > prev && toIndex <= prev) return prev + 1;
                return prev;
            });
        },
        [updateConfig]
    );

    const updateMappingActions = useCallback(
        (mappingIndex: number, updater: (actions: Action[]) => Action[]) => {
            updateConfig((prev) => ({
                ...prev,
                output_mapping: prev.output_mapping.map((m, i) =>
                    i === mappingIndex ? { ...m, actions: updater(m.actions) } : m
                ),
            }));
        },
        [updateConfig]
    );

    const addAction = useCallback(
        (mappingIndex: number, actionType: ActionType, insertIndex?: number) => {
            const newAction = createDefaultAction(actionType);
            updateMappingActions(mappingIndex, (actions) => {
                const copy = [...actions];
                copy.splice(insertIndex ?? copy.length, 0, newAction);
                return copy;
            });
        },
        [updateMappingActions]
    );

    const updateAction = useCallback(
        (mappingIndex: number, actionIndex: number, updates: Partial<Action>) => {
            updateMappingActions(mappingIndex, (actions) =>
                actions.map((a, j) => (j === actionIndex ? ({ ...a, ...updates } as Action) : a))
            );
        },
        [updateMappingActions]
    );

    const deleteAction = useCallback(
        (mappingIndex: number, actionIndex: number) => {
            updateMappingActions(mappingIndex, (actions) => actions.filter((_, j) => j !== actionIndex));
        },
        [updateMappingActions]
    );

    const moveAction = useCallback(
        (mappingIndex: number, fromIndex: number, toIndex: number) => {
            updateMappingActions(mappingIndex, (actions) => {
                const copy = [...actions];
                const [removed] = copy.splice(fromIndex, 1);
                copy.splice(toIndex, 0, removed);
                return copy;
            });
        },
        [updateMappingActions]
    );

    const addCustomField = useCallback(
        (typeName: string, fieldPath: string[], field: FieldDefinition) => {
            updateConfig((prev) => ({
                ...prev,
                input_types: prev.input_types.map((type) =>
                    type.name === typeName
                        ? {
                              ...type,
                              fields: updateFieldsAtPath(type.fields, fieldPath, (fs) => [
                                  ...fs,
                                  { ...field, is_custom: true },
                              ]),
                          }
                        : type
                ),
            }));
        },
        [updateConfig]
    );

    const removeCustomField = useCallback(
        (typeName: string, fieldPath: string[]) => {
            const parentPath = fieldPath.slice(0, -1);
            const fieldName = fieldPath[fieldPath.length - 1];
            updateConfig((prev) => ({
                ...prev,
                input_types: prev.input_types.map((type) =>
                    type.name === typeName
                        ? {
                              ...type,
                              fields: updateFieldsAtPath(type.fields, parentPath, (fs) =>
                                  fs.filter((f) => f.name !== fieldName || !f.is_custom)
                              ),
                          }
                        : type
                ),
            }));
        },
        [updateConfig]
    );

    const typeMap = useMemo(() => {
        const map = new Map<string, TypeDefinition>();
        config.input_types.forEach((type) => map.set(type.name, type));
        const registerEmbedded = (fields: FieldDefinition[]) => {
            fields.forEach((field) => {
                if (!field.nested_fields) return;
                const typeName = field.item_type || field.object_type;
                if (typeName && !map.has(typeName)) {
                    map.set(typeName, { name: typeName, fields: field.nested_fields });
                }
                registerEmbedded(field.nested_fields);
            });
        };
        config.input_types.forEach((type) => registerEmbedded(type.fields));
        return map;
    }, [config.input_types]);

    // Single source of truth for walking the input field tree (nested_fields + object_type refs,
    // with cycle detection). flattenFields / getInputFields are thin wrappers over it.
    const walkFields = useCallback(
        (
            fields: FieldDefinition[],
            prefix: string,
            visit: (path: string, field: FieldDefinition) => void,
            visited = new Set<string>()
        ): void => {
            fields.forEach((field) => {
                const path = prefix ? `${prefix}.${field.name}` : field.name;
                visit(path, field);
                if (field.nested_fields) walkFields(field.nested_fields, path, visit, visited);
                if (field.object_type && !visited.has(field.object_type)) {
                    const ref = typeMap.get(field.object_type);
                    if (ref) {
                        const newVisited = new Set(visited);
                        newVisited.add(field.object_type);
                        walkFields(ref.fields, path, visit, newVisited);
                    }
                }
            });
        },
        [typeMap]
    );

    const flattenFields = useCallback(
        (fields: FieldDefinition[], prefix = ''): string[] => {
            const paths: string[] = [];
            walkFields(fields, prefix, (path) => paths.push(path));
            return paths;
        },
        [walkFields]
    );

    // One walk yields every source path with its description. chain_value and bare iteration
    // aliases carry synthetic descriptions so callers never special-case them.
    const getInputFields = useCallback(
        (aliasContext: AliasContextEntry[] = []): InputFieldOption[] => {
            const fields: InputFieldOption[] = [];
            const seen = new Set<string>();
            const collect = (typeFields: FieldDefinition[], prefix: string, fallback?: string) =>
                walkFields(typeFields, prefix, (path, field) => {
                    if (seen.has(path)) return;
                    seen.add(path);
                    fields.push({ path, description: field.description ?? fallback });
                });
            config.input_types.forEach((type) => collect(type.fields, type.name));
            fields.push({ path: 'chain_value', description: 'Use the value from previous action' });
            seen.add('chain_value');
            aliasContext.forEach((entry) => {
                if (!seen.has(`@${entry.alias}`)) {
                    seen.add(`@${entry.alias}`);
                    fields.push({ path: `@${entry.alias}`, description: 'Iteration variable' });
                }
                if (entry.typeName) {
                    const itemType = typeMap.get(entry.typeName);
                    if (itemType) collect(itemType.fields, `@${entry.alias}`, 'Iteration variable');
                }
            });
            return fields;
        },
        [config.input_types, walkFields, typeMap]
    );

    const getInputFieldPaths = useCallback(
        (aliasContext: AliasContextEntry[] = []): string[] => getInputFields(aliasContext).map((field) => field.path),
        [getInputFields]
    );

    const availableActionTypes = useMemo((): ActionTypeInfo[] => {
        const raw: ActionTypeInfo[] = config.action_types?.length
            ? config.action_types.map((at) => ({
                  value: at.name as ActionType,
                  label: at.label ?? toHumanReadable(at.name),
                  description: at.description ?? '',
              }))
            : ACTION_TYPES_FALLBACK;
        return raw.map((at) => ({
            ...at,
            label: t(`outputMapper.visualEditor.actionType.${at.value}`, { defaultValue: at.label }),
            description: t(`outputMapper.visualEditor.actionType.${at.value}.description`, {
                defaultValue: at.description,
            }),
        }));
    }, [config.action_types, t]);

    const fieldTypes = useMemo((): { value: string; label: string }[] => {
        const raw = config.field_types?.length ? config.field_types : FIELD_TYPES;
        return raw.map((ft) => ({
            value: ft.value,
            label: t(`outputMapper.visualEditor.fieldType.${ft.value}`, { defaultValue: ft.label }),
        }));
    }, [config.field_types, t]);

    const conditionOperators = useMemo((): { value: string; label: string }[] => {
        const raw = config.condition_operators?.length ? config.condition_operators : CONDITION_OPERATORS;
        return raw.map((op) => ({
            value: op.value,
            label: t(`outputMapper.visualEditor.conditionOperator.${op.value}`, { defaultValue: op.label }),
        }));
    }, [config.condition_operators, t]);

    const transformFunctions = useMemo((): TransformFunctionInfo[] => {
        const raw: TransformFunctionInfo[] = config.transform_functions?.length
            ? config.transform_functions.map((tf) => {
                  const fallback = TRANSFORM_FUNCTIONS.find((f) => f.name === tf.name);
                  return {
                      name: tf.name as TransformFunctionInfo['name'],
                      label: tf.label ?? fallback?.label ?? toHumanReadable(tf.name),
                      description: tf.description ?? fallback?.description ?? '',
                      category: (tf.category as TransformFunctionInfo['category']) ?? fallback?.category ?? 'string',
                      params: fallback?.params ?? [],
                  };
              })
            : TRANSFORM_FUNCTIONS;
        return raw.map((fn) => ({
            ...fn,
            label: t(`outputMapper.visualEditor.transformFunction.${fn.name}`, { defaultValue: fn.label }),
            description: t(`outputMapper.visualEditor.transformFunction.${fn.name}.description`, {
                defaultValue: fn.description,
            }),
        }));
    }, [config.transform_functions, t]);

    const value: OutputMapperEditorContextValue = useMemo(
        () => ({
            config,
            setConfig,
            isDirty,
            selectedMappingIndex,
            setSelectedMappingIndex,
            selectedMapping,
            addMapping,
            updateMapping,
            deleteMapping,
            moveMapping,
            addAction,
            updateAction,
            deleteAction,
            moveAction,
            addCustomField,
            removeCustomField,
            getInputFieldPaths,
            getInputFields,
            flattenFields,
            availableActionTypes,
            fieldTypes,
            conditionOperators,
            transformFunctions,
        }),
        [
            config,
            isDirty,
            selectedMappingIndex,
            selectedMapping,
            addMapping,
            updateMapping,
            deleteMapping,
            moveMapping,
            addAction,
            updateAction,
            deleteAction,
            moveAction,
            addCustomField,
            removeCustomField,
            getInputFieldPaths,
            getInputFields,
            flattenFields,
            availableActionTypes,
            fieldTypes,
            conditionOperators,
            transformFunctions,
        ]
    );

    return <OutputMapperEditorContext.Provider value={value}>{children}</OutputMapperEditorContext.Provider>;
};

export default OutputMapperEditorProvider;
